import React from 'react';
import usePlatform from '../utils/usePlatform';
import { appUrl } from '../utils/appUrl';
import { storeUrl } from '../utils/storeUrl';

export const GOOGLE_PLAY_URL =
  process.env.REACT_APP_GOOGLE_PLAY_URL;

export const APP_STORE_URL = process.env.REACT_APP_APP_STORE_URL;

const AppleIcon = () => (
  <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
  </svg>
);

export const PlayIcon = ({ size = 'w-6 h-6' }) => (
  <svg className={size} viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M3.6 1.8a1.6 1.6 0 0 0-.6 1.3v17.8a1.6 1.6 0 0 0 .6 1.3L13.8 12 3.6 1.8z" />
    <path fill="#34A853" d="M17.2 8.4L4.7 1.3a1.6 1.6 0 0 0-1.1-.5L13.8 12l3.4-3.6z" />
    <path fill="#EA4335" d="M13.8 12L3.6 22.2c.3.3.8.4 1.1.2l12.5-7.2L13.8 12z" />
    <path fill="#FBBC04" d="M17.2 8.4L13.8 12l3.4 3.2 3.6-2.1c.8-.5.8-1.6 0-2.1l-3.6-2.6z" />
  </svg>
);

const badgeBase =
  'group inline-flex items-center gap-3 rounded-xl border bg-black shadow-lg shadow-black/40 transition-all';

/* Store badges. Google Play and the App Store are both live; the visitor's
   platform decides which badge comes first and gets the glow. */
export default function StoreBadges({ align = 'center', label = true, tone = 'dark', web = true, testidPrefix = 'store' }) {
  const platform = usePlatform();
  const isAndroid = platform === 'android';
  const isIos = platform === 'ios';

  const alignment =
    align === 'left' ? 'items-start' : align === 'lg-left' ? 'items-center lg:items-start' : 'items-center';
  const labelClass = tone === 'light' ? 'text-gray-500' : 'text-purple-200/60';
  const labelText = isAndroid
    ? 'Free on Google Play'
    : isIos
      ? 'Free on the App Store'
      : 'Free on the App Store and Google Play';

  const play = (
    <a
      key="play"
      href={storeUrl(testidPrefix.split('-')[0])}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`${testidPrefix}-android`}
      title="Get Recalibrate on Google Play"
      className={`${badgeBase} ${
        isAndroid
          ? 'border-emerald-400/70 px-5 py-3 ring-2 ring-emerald-400/30 hover:-translate-y-0.5 hover:ring-emerald-400/60'
          : 'border-white/25 px-4 py-2.5 hover:-translate-y-0.5 hover:border-white/50'
      }`}
    >
      <PlayIcon size={isAndroid ? 'w-7 h-7' : 'w-6 h-6'} />
      <span className="text-left leading-tight">
        <span className="block text-[9px] font-medium uppercase tracking-[0.14em] text-white/60">
          {isAndroid ? 'Free download' : 'Get it on'}
        </span>
        <span className={`block font-semibold text-white ${isAndroid ? 'text-base' : 'text-[15px]'}`}>Google Play</span>
      </span>
    </a>
  );

  const ios = (
    <a
      key="ios"
      href={APP_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`${testidPrefix}-ios`}
      title="Download Recalibrate on the App Store"
      className={`${badgeBase} ${
        isIos
          ? 'border-sky-300/70 px-5 py-3 ring-2 ring-sky-300/30 hover:-translate-y-0.5 hover:ring-sky-300/60'
          : 'border-white/25 px-4 py-2.5 hover:-translate-y-0.5 hover:border-white/50'
      }`}
    >
      <AppleIcon />
      <span className="text-left leading-tight">
        <span className="block text-[9px] font-medium tracking-[0.04em] text-white/70">Download on the</span>
        <span className={`block font-semibold text-white ${isIos ? 'text-base' : 'text-[15px]'}`}>App Store</span>
      </span>
    </a>
  );

  return (
    <div className={`flex flex-col gap-2 ${alignment}`} data-testid={`${testidPrefix}-badges`} data-platform={platform}>
      {label && (
        <span className={`text-[10px] font-bold uppercase tracking-[0.2em] ${labelClass}`}>
          {labelText}
        </span>
      )}
      <div className="flex flex-wrap items-center justify-center gap-3">
        {isIos ? [ios, play] : [play, ios]}
      </div>
      {web && (
        <a
          href={appUrl('/', `${testidPrefix}-web-app`, 'store-badges')}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${testidPrefix}-web`}
          className={`text-xs underline-offset-4 transition-colors hover:underline ${
            tone === 'light' ? 'text-gray-500 hover:text-gray-800' : 'text-purple-200/70 hover:text-white'
          }`}
        >
          Prefer your browser? Use the web app
        </a>
      )}
    </div>
  );
}
